import { memo, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import _map from 'lodash/map';
// Local files
import ClinicianAutocomplete from 'components/Clinicians/Autocomplete/Autocomplete';
import SuperbillGoalEmails from '../Emails/Emails';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuperbillGoals from 'hooks/useSuperbillGoals';
import useApp from 'hooks/useApp';
import useSuccess from 'hooks/useSuccess';
import { DELETE_SUPERBILL_GOAL } from 'helpers/confirmations';
import { superbillGoalsSelectors } from 'features/superbillGoals';
import { appSelectors } from 'features/app'
import { checkValidEmail } from 'helpers';

const SuperbillGoalsDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const { openDeleteDialog, setSuperbillGoalDrawer } = useApp();
  const { getSuperbillGoal, createSuperbillGoal, updateSuperbillGoal, clearLocalActiveSuperbillGoal } = useSuperbillGoals();
  const { setSuccess } = useSuccess();
  const fetchedSuperbillGoal = useCustomSelector(superbillGoalsSelectors.getSuperbillGoalData);
  const { open, edit, id } = useCustomSelector(appSelectors.getSuperbillGoalDrawerData);
  const fetching = useCustomSelector(superbillGoalsSelectors.superbillGoalIsFetching);
  const [emails, setEmails] = useState([]);
  const [clinician, setClinician] = useState({ value: '', formattedValue: null, error: '' });

  const handleClose = () => setSuperbillGoalDrawer({ open: false, edit: false, id: '' });
  const fetchSuperbillGoal = (id) => {
    getSuperbillGoal(id)
    .then(({ payload, error }) => {
      if (!error) {
        const { superbill_goal } = payload;
        handleSetData(superbill_goal);
      }
    })
  };
  const handleSetData = (superbill_goal) => {
    setEmails(superbill_goal?.emails);
    setClinician({value: superbill_goal.clinician.id, formattedValue: superbill_goal.clinician, error: '' });
  };
  const handleEntering = () => {
    if (edit) {
      fetchSuperbillGoal(id);
    } 
  };
  const handleExited = () => { 
    setEmails([]);
    setClinician({ value: '', formattedValue: null, error: '' });
    clearLocalActiveSuperbillGoal();
  };
  const needToUpdate = (a,b) => a.emails !== b.emails || a.clinician !== b.localClinician;
  const handleSave = () => {
    let hasError = false;

    if (!clinician.value) {
      setClinician(prev => ({ ...prev, error: 'Clinician is empty' }) );
      hasError = true;
    }
    _map(emails, e => {
      if (!checkValidEmail(e)) hasError = true;
    })
    
    if (hasError) return;

    const superbillGoal = { 
      ...(edit && { id: fetchedSuperbillGoal.id }),
      emails, 
      clinician_id: clinician.value,
      localClinician: { id: clinician.formattedValue.id, first_name: clinician.formattedValue.first_name, last_name: clinician.formattedValue.last_name}
    };

    if (edit) {
      if (needToUpdate(fetchedSuperbillGoal, superbillGoal)) {
        updateSuperbillGoal(superbillGoal)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Recipient successfully updated');
            handleClose();
          }
        })
      } else {  
        handleClose();
      }
    } else {
      createSuperbillGoal(superbillGoal)
      .then(({ error }) => {
        if (!error) {
          setSuccess('Recipient successfully created');
          handleClose();
        }
      })
    }
  };
  
  const handleDelete = () => {
    handleClose();
    openDeleteDialog({ id: fetchedSuperbillGoal.id, type: DELETE_SUPERBILL_GOAL });
  };

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      onClose={handleClose}
      SlideProps={{ onExited: handleExited, onEntering: handleEntering }}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
          {edit && <Button color='error' disabled={fetching} onClick={handleDelete} sx={{ fontWeight: 700 }}>Delete</Button>}
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
          {!edit ? 'Create' : 'Edit'} Recipient
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
        <ClinicianAutocomplete
          id='category'
          label='Select clinician'
          fullWidth
          margin='dense'
          value={clinician.formattedValue}
          error={!!clinician.error}
          helperText={clinician.error}
          onChange={({ value, formattedValue, error }) => setClinician({ value, formattedValue, error })}
        />
        <SuperbillGoalEmails emails={emails} setEmails={v => setEmails(v)} />
        <Button
          variant='contained'
          sx={{ display: 'flex', mx: 'auto', px: 5, mt: 3 }}
          disabled={fetching}
          onClick={handleSave}
        >
          Save
        </Button>
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <SuperbillGoalsDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);